import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { createCheckout } from "@/lib/api";

const PERKS = [
  "Unlock all 11 learning categories",
  "Simple Words, Spanish, Geography & more",
  "Money Smarts, Team Building, Creativity",
];

export default function UpgradeModal({ open, onClose, profile }) {
  const [loading, setLoading] = useState(null);

  const go = async (lookupKey) => {
    setLoading(lookupKey);
    try {
      const { checkout_url } = await createCheckout(lookupKey, profile.id);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(null);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="upgrade-modal"
        >
          <motion.div
            initial={{ scale: 0.85, y: 30 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 18 }}
            className="bg-white rounded-[2rem] border-4 border-slate-900/10 p-8 w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-fredoka text-3xl font-bold text-slate-900 flex items-center gap-2">
                <Sparkles size={28} strokeWidth={3} className="text-yellow-500" />
                Go Premium
              </h2>
              <button
                data-testid="close-upgrade-button"
                onClick={onClose}
                className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center"
              >
                <X size={24} strokeWidth={3} className="text-slate-500" />
              </button>
            </div>
            <p className="text-base font-bold text-slate-400 mb-5">
              Unlock every learning adventure for {profile.name}!
            </p>

            <div className="space-y-2 mb-7">
              {PERKS.map((p) => (
                <div key={p} className="flex items-center gap-2 text-slate-700 font-bold">
                  <span className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                    <Check size={16} strokeWidth={4} className="text-green-600" />
                  </span>
                  <span className="text-base">{p}</span>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <button
                data-testid="buy-premium-button"
                onClick={() => go("premium_unlock")}
                disabled={loading}
                className="toy-btn w-full flex items-center justify-between bg-blue-500 text-white px-6 py-4 rounded-2xl"
                style={{ boxShadow: "0 6px 0 0 rgba(37,99,235,1)" }}
              >
                <span className="text-left">
                  <span className="font-fredoka text-xl font-bold block leading-tight">Unlock Premium</span>
                  <span className="text-sm font-bold text-white/80">One-time · forever</span>
                </span>
                <span className="font-fredoka text-2xl font-bold flex items-center gap-2">
                  {loading === "premium_unlock" ? <Loader2 className="animate-spin" /> : "$4.99"}
                </span>
              </button>

              <button
                data-testid="buy-pro-button"
                onClick={() => go("pro_monthly")}
                disabled={loading}
                className="toy-btn w-full flex items-center justify-between bg-pink-500 text-white px-6 py-4 rounded-2xl"
                style={{ boxShadow: "0 6px 0 0 rgba(190,24,93,1)" }}
              >
                <span className="text-left">
                  <span className="font-fredoka text-xl font-bold block leading-tight">Pro Plan</span>
                  <span className="text-sm font-bold text-white/80">Monthly subscription</span>
                </span>
                <span className="font-fredoka text-2xl font-bold flex items-center gap-2">
                  {loading === "pro_monthly" ? <Loader2 className="animate-spin" /> : "$2.99"}
                  <span className="text-sm font-bold">/mo</span>
                </span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
